// ── 월 급여 관리 탭 ──────────────────────────────────────────────────────────
// 1. 연/월 선택 → 급여 계산 실행
// 2. 직원별 급여 요약 테이블 (지급합계·공제합계·실수령액)
// 3. 행 클릭 → 상세/조정 모달
// 4. 확정 / 확정취소 / 재계산 버튼
// 5. 계산 모드 자동 판정: 현재/미래 월 → projected, 지난 월 → actual

import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../../api/apiClient';
import MonthNavigator from '../../../components/MonthNavigator';
import { useToast } from '../../../components/Toast';
import PayrollRecordModal from './PayrollRecordModal';
import PayrollRecordList from './PayrollRecordList';
import PayrollUploadSection from './PayrollUploadSection';
import PayrollWarningsPanel from './PayrollWarningsPanel';
import { buildPayrollConfirmMessage } from './payrollWarningHelpers';
import { printAllPayslips } from './payslipPrint';
import { downloadTaxOfficeSubmissionImage } from './taxOfficeSubmissionImage';
import { downloadPayrollTransferExcel } from './payrollTransferExcel';
import { isPayrollTargetForPeriod } from './payrollEligibility';
import { nowKST } from '../../../utils/date';
import { stdBlackBtn } from '../adminlayout/designStandard';
import { usePayrollPeriod } from './usePayrollPeriod';
import { useCompanyScope } from '../adminlayout/CompanyScopeContext';

// ── 모바일 감지 훅 (768px 이하) ─────────────────────────────────────────────
function useIsMobile() {
  const [m, setM] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const h = () => setM(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return m;
}

// ── 숫자 포맷 (3자리 콤마) ───────────────────────────────────────────────────
const fmt = (n) => n != null ? Number(n).toLocaleString('ko-KR') : '0';

// ── 스타일 상수 ──────────────────────────────────────────────────────────────
const cardStyle = {
  background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)',
  boxShadow: 'none', padding: '16px 20px',
};
// 주요 CTA — 디자인 표준(stdBlackBtn)
const btnPrimary = { ...stdBlackBtn };
const btnOutline = {
  border: '1px solid var(--border)', borderRadius: 8, padding: '6px 14px',
  background: 'var(--bg-card)', color: 'var(--text-sub)', fontWeight: 600, fontSize: 12, cursor: 'pointer',
};
const btnDanger = {
  border: '1px solid var(--border)', borderRadius: 8, padding: '6px 14px',
  background: 'var(--bg-card)', color: '#dc2626', fontWeight: 600, fontSize: 12, cursor: 'pointer',
};
// 상태 배지 색상 맵 (표준 톤 — 보라 제거)
const STATUS_MAP = {
  draft:      { label: '초안', bg: '#94a3b8', color: '#fff' },
  calculated: { label: '계산완료', bg: 'var(--std-accent, #1d7de6)', color: '#fff' },
  confirmed:  { label: '확정', bg: '#16a34a', color: '#fff' },
};

export default function PayrollMonthly({ companyCode }) {
  const { showToast } = useToast();
  const isMobile = useIsMobile();
  // 볼 달·기간 불러오기는 usePayrollPeriod.js 로 옮김(2026-10-28, 300줄 규칙)
  const { year, month, defaultMonthReady, periodData, loading, fetchPeriod, handleMonthChange, handleDefaultMonth } = usePayrollPeriod(companyCode);
  const [calculating, setCalculating] = useState(false);

  // 모달
  const [modalRecordId, setModalRecordId] = useState(null);

  // ── 회사명 조회 (명세서 인쇄용) ─────────────────────────────────────────────
  //   본사면 사이트 이름(/api/site-info), 외부 회사면 법인명 — 'PINE' 판정 없앰(2026-10-28 'PINE' 정리 8-2, 미팅링크 명세서에 'LINK' 가 찍혔다)
  const { hqCompanyCode, hqReady } = useCompanyScope() || {};
  const [companyName, setCompanyName] = useState('');
  useEffect(() => {
    if (!hqReady) return;
    const isHq = String(companyCode || '').toUpperCase() === String(hqCompanyCode || '').toUpperCase();
    if (!companyCode || isHq) {
      apiClient.get('/api/site-info').then((res) => setCompanyName(res.ok ? String(res.data?.data?.name || '') : ''));
      return;
    }
    // 외부 회사 목록에서 회사명 조회 (company_name이 정식 명칭, 없으면 name 사용)
    apiClient.get('/api/my/external-companies').then((res) => {
      if (res.ok && res.data?.list) {
        const co = res.data.list.find((c) => c.code.toUpperCase() === companyCode.toUpperCase());
        if (co) setCompanyName(co.company_name || co.name || companyCode);
        else setCompanyName(companyCode);
      }
    });
  }, [companyCode, hqCompanyCode, hqReady]);


  // ── 계산 모드 자동 판정: 현재/미래 월 → projected, 지난 월 → actual ──────
  // nowKST()는 UTC 메서드로 KST 년/월 반환 (utils/date.js 규칙)
  const getCalcMode = useCallback((y, m) => {
    const n = nowKST();
    const curYear = n.getUTCFullYear();
    const curMonth = n.getUTCMonth() + 1;
    // 지난 달: actual (실적 기준), 이번 달 이후: projected (만근 예상)
    if (y < curYear || (y === curYear && m < curMonth)) return 'actual';
    return 'projected';
  }, []);

  // ── 급여 계산 실행 (모드 자동 판정) ───────────────────────────────────────
  const handleCalculate = async () => {
    const mode = getCalcMode(year, month);
    setCalculating(true);
    const res = await apiClient.post('/api/admin/payroll/monthly/calculate', {
      company_code: companyCode, year, month,
    });
    setCalculating(false);
    if (res.ok) {
      showToast(`${year}년 ${month}월 급여가 ${mode === 'projected' ? '예상(만근) ' : ''}계산되었습니다.`, 'success');
      fetchPeriod();
    } else {
      showToast(res.data?.message || '계산 실패', 'error');
    }
  };

  // ── 확정 ──────────────────────────────────────────────────────────────────
  const handleConfirm = async () => {
    if (!periodData?.period?.id) return;
    if (!window.confirm(buildPayrollConfirmMessage(periodData?.warnings || []))) return;
    const res = await apiClient.post(`/api/admin/payroll/monthly/periods/${periodData.period.id}/confirm`);
    if (res.ok) { showToast('급여가 확정되었습니다.', 'success'); fetchPeriod(); }
    else showToast(res.data?.message || '확정 실패', 'error');
  };

  // ── 확정 취소 ─────────────────────────────────────────────────────────────
  const handleReopen = async () => {
    if (!periodData?.period?.id) return;
    if (!window.confirm('확정을 취소하시겠습니까?')) return;
    const res = await apiClient.post(`/api/admin/payroll/monthly/periods/${periodData.period.id}/reopen`);
    if (res.ok) { showToast('확정이 취소되었습니다.', 'success'); fetchPeriod(); }
    else showToast(res.data?.message || '확정 취소 실패', 'error');
  };

  const period = periodData?.period;
  const records = periodData?.records || [];
  const visibleRecords = records.filter((r) => isPayrollTargetForPeriod(r, period));
  const excludedRecords = records.filter((r) => !isPayrollTargetForPeriod(r, period));
  const payrollWarnings = periodData?.warnings || [];
  const status = period?.status;
  const statusInfo = STATUS_MAP[status] || { label: status || '미계산', bg: '#6b7280', color: '#fff' };
  const isConfirmed = status === 'confirmed';

  // 합계
  const totalEarn = visibleRecords.reduce((s, r) => s + Number(r.total_earning), 0);
  const totalDed  = visibleRecords.reduce((s, r) => s + Number(r.total_deduction), 0);
  const totalNet  = visibleRecords.reduce((s, r) => s + Number(r.net_pay), 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* ── 기간 선택 + 액션 버튼 ── */}
      <div style={{ ...cardStyle, display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', gap: 12, flexWrap: 'wrap' }}>
        {/* 연/월 선택 + 상태 배지 (한 줄) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <MonthNavigator
            year={year}
            month={month}
            onChange={handleMonthChange}
            onReset={handleDefaultMonth}
            showReset
            disabled={!defaultMonthReady || loading}
          />
          {/* 계산 모드 자동 표시 (현재/미래 월: 예상, 지난 월: 실적) */}
          <span style={{
            padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700,
            background: getCalcMode(year, month) === 'projected' ? '#fef3c7' : '#dbeafe',
            color: getCalcMode(year, month) === 'projected' ? '#92400e' : '#1e40af',
          }}>
            {getCalcMode(year, month) === 'projected' ? '예상(만근)' : '실적 기준'}
          </span>
          {/* 상태 배지 */}
          {period && (
            <span style={{
              padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 800,
              background: statusInfo.bg, color: statusInfo.color,
            }}>
              {statusInfo.label}
            </span>
          )}
        </div>

        {/* 액션 버튼들 — 모바일: 가로 스크롤, 데스크톱: 우측 정렬 */}
        <div style={{ marginLeft: isMobile ? 0 : 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {/* 계산/재계산 버튼 (확정 상태가 아닐 때) */}
          {!isConfirmed && (
            <button style={{ ...btnPrimary, ...(isMobile ? { flex: 1, minWidth: 0 } : {}) }} onClick={handleCalculate} disabled={calculating}>
              {calculating ? '계산 중...' : period ? '재계산' : '급여 계산'}
            </button>
          )}
          {/* 확정 버튼 (calculated + actual 모드에서만, 예상 급여는 확정 불가) */}
          {status === 'calculated' && period?.calculation_mode !== 'projected' && (
            <button style={{ ...btnOutline, borderColor: '#16a34a', color: '#16a34a' }} onClick={handleConfirm}>
              확정
            </button>
          )}
          {/* 확정 취소 버튼 (confirmed 상태에서만) */}
          {isConfirmed && (
            <button style={btnDanger} onClick={handleReopen}>확정 취소</button>
          )}
          {/* 급여명세서 일괄 인쇄 (계산된 데이터가 있을 때) */}
          {visibleRecords.length > 0 && (
            <button style={btnOutline} onClick={() => printAllPayslips(period.id, period, visibleRecords, companyName)}>
              명세서 전체 인쇄
            </button>
          )}
          {visibleRecords.length > 0 && (
            <button style={btnOutline} onClick={() => downloadTaxOfficeSubmissionImage({ period, records: visibleRecords, companyName })}>
              세무사제출
            </button>
          )}
          {isConfirmed && visibleRecords.length > 0 && (
            <button style={btnOutline} onClick={() => downloadPayrollTransferExcel({ period, showToast })}>
              급여이체 엑셀
            </button>
          )}
        </div>
      </div>

      {excludedRecords.length > 0 && (
        <div style={{
          ...cardStyle,
          padding: '10px 14px',
          borderColor: 'rgba(217,119,6,0.35)',
          background: 'rgba(217,119,6,0.08)',
          color: 'var(--text-sub)',
          fontSize: 12,
          lineHeight: 1.5,
        }}>
          <strong style={{ color: '#b45309' }}>급여대상 외 기록 {excludedRecords.length}건 확인 필요</strong>
          <span style={{ marginLeft: 8 }}>
            입사일/퇴사일이 이 급여기간과 겹치지 않아 목록, 명세서, 세무사제출, 급여이체 엑셀에서 제외됩니다.
            {' '}대상: {excludedRecords.map((r) => r.user_name).join(', ')}
          </span>
        </div>
      )}

      {/* ── 요약 카드 (모바일 2x2, 데스크톱 4x1) ── */}
      {visibleRecords.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: isMobile ? 8 : 12 }}>
          {[
            { label: '인원', value: `${visibleRecords.length}명`, color: 'var(--text)' },
            { label: '지급 합계', value: `${fmt(totalEarn)}원`, color: '#16a34a' },
            { label: '공제 합계', value: `${fmt(totalDed)}원`, color: '#dc2626' },
            { label: '실수령 합계', value: `${fmt(totalNet)}원`, color: 'var(--std-accent-text, #1d7de6)' },
          ].map((c) => (
            <div key={c.label} style={{ ...cardStyle, textAlign: 'center', padding: isMobile ? '10px 6px' : '12px 8px' }}>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4 }}>{c.label}</div>
              <div style={{ fontSize: isMobile ? 14 : 16, fontWeight: 900, color: c.color }}>{c.value}</div>
            </div>
          ))}
        </div>
      )}

      <PayrollWarningsPanel warnings={payrollWarnings} isMobile={isMobile} />

      {/* ── 세무사 급여대장 업로드 ── */}
      <PayrollUploadSection
        companyCode={companyCode}
        year={year}
        month={month}
        periodId={periodData?.period?.id}
        isConfirmed={isConfirmed}
        showToast={showToast}
        onUploaded={fetchPeriod}
      />

      {/* ── 직원별 급여 목록 (모바일: 카드, 데스크톱: 테이블) ── */}
      {loading ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>로딩 중...</div>
      ) : visibleRecords.length > 0 ? (
        <PayrollRecordList
          isMobile={isMobile} records={visibleRecords} period={period}
          totalEarn={totalEarn} totalDed={totalDed} totalNet={totalNet}
          companyName={companyName}
          onSelect={(id) => setModalRecordId(id)}
        />
      ) : (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>
          {period ? '급여대상 직원이 없습니다.' : `${year}년 ${month}월 급여가 아직 계산되지 않았습니다. "급여 계산" 버튼을 클릭하세요.`}
        </div>
      )}

      {/* ── 상세/조정 모달 ── */}
      {modalRecordId && (
        <PayrollRecordModal
          recordId={modalRecordId}
          isConfirmed={isConfirmed}
          period={period}
          companyName={companyName}
          onClose={() => setModalRecordId(null)}
          onSaved={fetchPeriod}
        />
      )}
    </div>
  );
}
